"use client";

import { useRef, useState } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";

import { MAX_FORUM_PHOTOS } from "@/lib/forum-images";

export function ForumPhotoGrid({ urls }: { urls?: string[] | null }) {
  if (!urls?.length) return null;
  return (
    <div className={`mt-3 grid gap-2 ${urls.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
      {urls.map((url) => (
        <a key={url} href={url} target="_blank" rel="noopener noreferrer" className="block">
          <img
            src={url}
            alt=""
            className="w-full max-h-72 object-cover rounded-xl border border-gray-200 bg-gray-50"
          />
        </a>
      ))}
    </div>
  );
}

export function ForumPhotoPicker({
  urls,
  onChange,
  disabled,
}: {
  urls: string[];
  onChange: (urls: string[]) => void;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const addFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setError("");
    setUploading(true);
    try {
      const next = [...urls];
      for (const file of Array.from(files)) {
        if (next.length >= MAX_FORUM_PHOTOS) break;
        const body = new FormData();
        body.set("file", file);
        const res = await fetch("/api/upload", { method: "POST", body });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.url) {
          throw new Error(data.error || "Couldn't upload that photo");
        }
        next.push(data.url);
      }
      onChange(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't upload that photo");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {urls.map((url) => (
          <div key={url} className="relative h-16 w-16">
            <img src={url} alt="" className="h-16 w-16 rounded-lg object-cover border border-gray-200" />
            <button
              type="button"
              className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-white border border-gray-200 text-gray-600 flex items-center justify-center"
              onClick={() => onChange(urls.filter((item) => item !== url))}
              disabled={disabled || uploading}
              aria-label="Remove photo"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
      {urls.length < MAX_FORUM_PHOTOS && (
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || uploading}
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
          Add photo
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => void addFiles(e.target.files)}
      />
      {error ? <p className="text-xs text-red-600">{error}</p> : null}
    </div>
  );
}
